import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { apiClient } from "@/infrastructure/api/client";
import {
  ApiError,
  type PollResponseDto,
  type CreatePollDto,
  type UpdatePollDto,
  type VotePollDto,
} from "@tabipla/shared";

// Query keys
export const pollKeys = {
  all: ["polls"] as const,
  lists: () => [...pollKeys.all, "list"] as const,
  list: (tripId: string) => [...pollKeys.lists(), tripId] as const,
  detail: (tripId: string, pollId: string) => [...pollKeys.all, "detail", tripId, pollId] as const,
};

// Get all polls for a trip
export function usePolls(tripId: string) {
  return useQuery({
    queryKey: pollKeys.list(tripId),
    queryFn: async () => {
      const response = await apiClient.get<PollResponseDto[]>(`/api/trips/${tripId}/polls`);
      if (!response.success) {
        throw ApiError.fromResponse(response.error, "Failed to fetch polls");
      }
      return response.data;
    },
    enabled: !!tripId,
  });
}

// Get a specific poll
export function usePoll(tripId: string, pollId: string) {
  return useQuery({
    queryKey: pollKeys.detail(tripId, pollId),
    queryFn: async () => {
      const response = await apiClient.get<PollResponseDto>(`/api/trips/${tripId}/polls/${pollId}`);
      if (!response.success) {
        throw ApiError.fromResponse(response.error, "Failed to fetch poll");
      }
      return response.data;
    },
    enabled: !!tripId && !!pollId,
  });
}

// Create a new poll
export function useCreatePoll(tripId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (data: CreatePollDto) => {
      const response = await apiClient.post<PollResponseDto>(`/api/trips/${tripId}/polls`, data);
      if (!response.success) {
        throw ApiError.fromResponse(response.error, "Failed to create poll");
      }
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: pollKeys.list(tripId) });
    },
  });
}

// Update a poll
export function useUpdatePoll(tripId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ pollId, data }: { pollId: string; data: UpdatePollDto }) => {
      const response = await apiClient.put<PollResponseDto>(
        `/api/trips/${tripId}/polls/${pollId}`,
        data
      );
      if (!response.success) {
        throw ApiError.fromResponse(response.error, "Failed to update poll");
      }
      return response.data;
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: pollKeys.list(tripId) });
      queryClient.invalidateQueries({ queryKey: pollKeys.detail(tripId, variables.pollId) });
    },
  });
}

// Close a poll
export function useClosePoll(tripId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (pollId: string) => {
      const response = await apiClient.post<PollResponseDto>(
        `/api/trips/${tripId}/polls/${pollId}/close`
      );
      if (!response.success) {
        throw ApiError.fromResponse(response.error, "Failed to close poll");
      }
      return response.data;
    },
    onSuccess: (_, pollId) => {
      queryClient.invalidateQueries({ queryKey: pollKeys.list(tripId) });
      queryClient.invalidateQueries({ queryKey: pollKeys.detail(tripId, pollId) });
    },
  });
}

// Delete a poll
export function useDeletePoll(tripId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (pollId: string) => {
      const response = await apiClient.delete(`/api/trips/${tripId}/polls/${pollId}`);
      if (!response.success) {
        throw ApiError.fromResponse(response.error, "Failed to delete poll");
      }
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: pollKeys.list(tripId) });
    },
  });
}

// Vote on a poll
export function useVotePoll(tripId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ pollId, data }: { pollId: string; data: VotePollDto }) => {
      const response = await apiClient.post<PollResponseDto>(
        `/api/trips/${tripId}/polls/${pollId}/vote`,
        data
      );
      if (!response.success) {
        throw ApiError.fromResponse(response.error, "Failed to vote on poll");
      }
      return response.data;
    },
    onMutate: async ({ pollId, data }) => {
      // Cancel any outgoing refetches
      await queryClient.cancelQueries({ queryKey: pollKeys.list(tripId) });

      // Snapshot the previous value
      const previousPolls = queryClient.getQueryData<PollResponseDto[]>(pollKeys.list(tripId));

      // Optimistically update to the new value
      if (previousPolls) {
        queryClient.setQueryData<PollResponseDto[]>(
          pollKeys.list(tripId),
          previousPolls.map((poll) => {
            if (poll.id !== pollId) return poll;

            const alreadyVoted = poll.myVotes.includes(data.optionIndex);
            let newMyVotes: number[];
            let voteDelta = 0;

            if (alreadyVoted) {
              // Remove vote
              newMyVotes = poll.myVotes.filter((v) => v !== data.optionIndex);
              voteDelta = -1;
            } else if (poll.allowMultiple) {
              // Add vote
              newMyVotes = [...poll.myVotes, data.optionIndex];
              voteDelta = 1;
            } else {
              // Replace vote (remove old, add new)
              newMyVotes = [data.optionIndex];
              voteDelta = poll.myVotes.length === 0 ? 1 : 0; // Only add if no previous vote
            }

            // Update results with new vote counts
            const updatedResults = poll.results.map((result) => {
              let newVotes = result.votes;

              if (result.optionIndex === data.optionIndex) {
                // Target option
                newVotes = Math.max(0, result.votes + voteDelta);
              } else if (!poll.allowMultiple && poll.myVotes.length > 0) {
                // Single choice: remove vote from previous option
                if (poll.myVotes.includes(result.optionIndex)) {
                  newVotes = Math.max(0, result.votes - 1);
                }
              }

              return {
                ...result,
                votes: newVotes,
              };
            });

            return {
              ...poll,
              myVotes: newMyVotes,
              results: updatedResults,
              totalVotes: Math.max(0, poll.totalVotes + voteDelta),
            };
          })
        );
      }

      return { previousPolls };
    },
    onSuccess: (data, variables) => {
      // Update cache with server response for accuracy
      if (data) {
        queryClient.setQueryData<PollResponseDto[]>(pollKeys.list(tripId), (previous) => {
          if (!previous) return previous;
          return previous.map((poll) => (poll.id === variables.pollId ? data : poll));
        });
      }
    },
    onError: (_, __, context) => {
      // Rollback on error
      if (context?.previousPolls) {
        queryClient.setQueryData(pollKeys.list(tripId), context.previousPolls);
      }
    },
    onSettled: (_, __, variables) => {
      queryClient.invalidateQueries({ queryKey: pollKeys.detail(tripId, variables.pollId) });
    },
  });
}
